import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE, page, cardScroll, inp, btn, btnDisabled } from './AdminUsers';
import TopBanner from '../components/TopBanner';
import AdminSidebar from '../components/AdminSidebar';

// Read-only view over Authentik's own Events API — see
// backend/src/routes/auditLog.js for why this doesn't build a separate
// logging system. Super-admin only, same gate as the rest of this
// sidebar section.

const ACTION_LABELS = {
  login: 'Login',
  logout: 'Logout',
  login_failed: 'Login failed',
  password_set: 'Password changed',
  user_write: 'Account updated',
  model_created: 'Record created',
  model_updated: 'Record updated',
  model_deleted: 'Record deleted',
  authorize_application: 'App authorized',
  invitation_used: 'Invite used',
  suspicious_request: 'Suspicious request',
  impersonation_started: 'Impersonation started',
  impersonation_ended: 'Impersonation ended',
  // Our own recorded actions (userActionLog.js) — Authentik has no
  // concept of these, they only ever come from that store.
  password_reset: 'Password reset',
  totp_setup: 'Authenticator set up done',
  webauthn_setup: 'Passkey set up',
  webauthn_deleted: 'Passkey removed',
  login_recovery_verified: 'Sign-in recovery: code verified',
  sms_mfa_setup: 'SMS sign-in set up',
  code_mfa_setup: 'One-time code sign-in set up',
  phone_updated: 'Phone updated',
  email_updated: 'Email updated',
  approved: 'Approved',
  rejected: 'Rejected',
  registration_identity_submitted: 'Registration: identity submitted',
  registration_phone_verified: 'Registration: phone verified',
  registration_email_confirmed: 'Registration: email confirmed',
  registration_email_verified: 'Registration: email verified',
  registration_profile_completed: 'Registration: profile completed',
  registration_approval_pending: 'Registration: pending approval',
};

const actionLabel = (a) => ACTION_LABELS[a] || a;

// overrideLabel (backend-computed, see auditLog.js's describeModelAction)
// wins when present — e.g. "Account created"/"Authenticator device
// created" instead of the generic model_created/model_updated label,
// derived from which Authentik model the event actually touched.
const displayLabel = (e) => e.overrideLabel || actionLabel(e.action);

const thStyle = {
  textAlign: 'left', padding: '10px 12px', fontSize: '12px', fontWeight: '700',
  color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em',
  borderBottom: '2px solid #eee', background: '#fafafa',
};
const tdStyle = { padding: '10px 12px', fontSize: '13px', color: '#2c2c2a', borderBottom: '1px solid #f0f0f0' };

const actionBadgeColor = (a) => {
  if (a === 'login') return { bg: '#E6F5EC', fg: '#1E7A46' };
  if (a === 'logout' || a === 'login_failed') return { bg: '#FDECEC', fg: '#B3261E' };
  if (a === 'password_set') return { bg: '#FFF3E0', fg: '#B25E00' };
  if (a.startsWith('model_')) return { bg: '#E6F5FC', fg: '#0072B1' };
  if (a === 'password_reset' || a === 'totp_setup' || a === 'webauthn_setup' || a === 'webauthn_deleted' || a === 'sms_mfa_setup' || a === 'code_mfa_setup' || a === 'phone_updated' || a === 'email_updated') return { bg: '#EAE6FB', fg: '#5B3EBF' };
  if (a === 'approved') return { bg: '#E6F5EC', fg: '#1E7A46' };
  if (a === 'rejected') return { bg: '#FDECEC', fg: '#B3261E' };
  if (a.startsWith('registration_')) return { bg: '#F0F0F0', fg: '#666' };
  return { bg: '#F0F0F0', fg: '#555' };
};

// `usernameFilter` (optional): pre-fills and locks the username filter —
// used when this is embedded as AdminUserDetail.jsx's own Activity
// section instead of rendered as the full standalone page.
// `userPk` (optional, embedded mode only): the target user's Authentik
// pk. Admin-initiated actions on this user (phone/email updates, group
// changes, etc.) go through this app's own service account
// (AUTHENTIK_API_TOKEN), so Authentik attributes them to that service
// account as the actor, NOT this user — usernameFilter alone misses
// them entirely. userPk lets the backend also match on "what was done
// TO this user's record" regardless of who did it.
// `clean` (optional): curated view — login, logout, app access, password
// reset, authenticator setup only. Excludes the raw flow-executor churn
// and registration-stage-wise entries, which stay full-detail-only.
export default function AdminAuditLog({ usernameFilter, userPk, embedded, clean }) {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [events, setEvents] = useState([]);
  const [actions, setActions] = useState([]);
  // Standalone mode (no usernameFilter prop) also accepts ?username= in
  // the URL — lets AdminUserDetail.jsx's "view full history" link deep-
  // link here pre-filled for that user.
  const [username, setUsername] = useState(usernameFilter || searchParams.get('username') || '');
  const [action, setAction] = useState('');
  const [pageNum, setPageNum] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Guard: only bescom-super-admins. Two cases, not one — checking the
  // group only when `user` is already truthy would silently do nothing
  // for a visitor with NO session at all — see AdminApprovals.jsx's own
  // comment on this same pattern.
  useEffect(() => {
    if (embedded) return; // AdminUserDetail.jsx already gates the whole page
    if (authLoading) return;
    if (!user) {
      navigate('/');
      return;
    }
    const groups = user.profile?.groups || [];
    if (!groups.includes('bescom-super-admins')) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate, embedded]);

  useEffect(() => {
    if (embedded) return;
    const params = clean ? '?clean=true' : '';
    fetch(`${API_BASE}/admin/audit-log/actions${params}`, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => setActions(data.actions || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEvents = useCallback(async (p) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(p) });
      if (username) params.set('username', username);
      if (action) params.set('action', action);
      if (userPk) params.set('userPk', String(userPk));
      if (clean) params.set('clean', 'true');
      const res = await fetch(`${API_BASE}/admin/audit-log?${params.toString()}`, {
        credentials: 'include',
      });
      if (res.status === 401) {
        navigate('/', { state: { sessionExpired: true } });
        return;
      }
      if (res.status === 403) {
        setError('You are not authorized to view the audit log.');
        return;
      }
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not load the audit log.');
        return;
      }
      setEvents(data.events || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setCount(data.pagination?.count || 0);
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, action, navigate]);

  useEffect(() => {
    setPageNum(1);
    fetchEvents(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, action, userPk, clean]);

  function goToPage(p) {
    setPageNum(p);
    fetchEvents(p);
  }

  const tableSection = (
    <>
      {!embedded && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <input
            style={{ ...inp, maxWidth: '240px' }}
            placeholder="Filter by username…"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <select style={{ ...inp, maxWidth: '220px' }} value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="">All actions</option>
            {actions.map((a) => (
              <option key={a} value={a}>{actionLabel(a)}</option>
            ))}
          </select>
        </div>
      )}

      {error && (
        <div style={{ background: '#FDECEC', color: '#B3261E', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>Loading…</p>
      ) : events.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
          <p style={{ fontSize: '32px', margin: '0 0 16px' }}>📭</p>
          <p>No activity found.</p>
        </div>
      ) : (
        <>
          <div style={{ border: '1px solid #eee', borderRadius: '10px', overflow: 'hidden', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
              <thead>
                <tr>
                  <th style={thStyle}>When</th>
                  <th style={thStyle}>User</th>
                  <th style={thStyle}>Action</th>
                  <th style={thStyle}>Application</th>
                  <th style={thStyle}>Detail</th>
                  <th style={thStyle}>IP</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => {
                  const colors = actionBadgeColor(e.action);
                  return (
                    <tr key={e.id}>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: '#666' }}>
                        {new Date(e.createdAt).toLocaleString()}
                      </td>
                      <td style={{ ...tdStyle, fontWeight: '600' }}>
                        {e.username || '—'}
                        {/* Admin-initiated actions on someone else's account (see
                            userActionLog.js) — actor and target differ, so show both:
                            who did it, and who it happened to. */}
                        {e.targetUsername && e.targetUsername !== e.username && (
                          <span style={{ fontWeight: '600', color: '#0072B1' }}> → {e.targetUsername}</span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          fontSize: '11px', fontWeight: '700', color: colors.fg, background: colors.bg,
                          padding: '3px 10px', borderRadius: '10px', whiteSpace: 'nowrap',
                        }}>
                          {displayLabel(e)}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, fontWeight: e.application ? '600' : '400', color: e.application ? '#0072B1' : '#bbb' }}>
                        {e.application || '—'}
                        {/* Only ever present on authorize_application events, and only
                            when Authentik's own event actually captured redirect_uri —
                            see auditLog.js's describePlatform for why this tells apart
                            a web login from a native-app one sharing the same
                            client_id/Application (e.g. Transfer's Android app). */}
                        {e.platform && (
                          <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: '600', color: '#888', background: '#F1EFE8', padding: '2px 8px', borderRadius: '10px' }}>
                            {e.platform}
                          </span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, color: '#666', fontFamily: e.detail ? 'inherit' : 'monospace', fontSize: e.detail ? '13px' : '12px' }}>
                        {e.detail || (e.requestMethod && e.requestPath ? `${e.requestMethod} ${e.requestPath}` : (e.model || '—'))}
                      </td>
                      <td style={{ ...tdStyle, color: '#666' }}>{e.ip || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
            <span style={{ fontSize: '12px', color: '#888' }}>{count} total events</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={() => goToPage(pageNum - 1)}
                disabled={pageNum <= 1}
                style={pageNum <= 1 ? { ...btnDisabled, padding: '8px 16px', fontSize: '13px' } : { ...btn, padding: '8px 16px', fontSize: '13px' }}
              >
                ← Previous
              </button>
              <span style={{ fontSize: '13px', color: '#666', alignSelf: 'center' }}>
                Page {pageNum} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => goToPage(pageNum + 1)}
                disabled={pageNum >= totalPages}
                style={pageNum >= totalPages ? { ...btnDisabled, padding: '8px 16px', fontSize: '13px' } : { ...btn, padding: '8px 16px', fontSize: '13px' }}
              >
                Next →
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );

  if (embedded) return tableSection;

  return (
    <>
      <TopBanner />
      <div className="admin-page-wrap" style={page}>
        <div className="admin-row" style={{ display: 'flex', gap: '24px', width: '100%', height: '100%' }}>
          <AdminSidebar active={clean ? 'user-activity' : 'audit-log'} />
          <div style={cardScroll}>
            <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 4px' }}>
              {clean ? 'User Activity' : 'Audit Log'}
            </h1>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '24px' }}>
              {clean
                ? 'Login, logout, app access, password reset, and authenticator setup across every user — newest first.'
                : 'Every login, logout, password change, and admin action across SSO and Authentik — newest first.'}
            </p>
            {tableSection}
          </div>
        </div>
      </div>
    </>
  );
}
